import { useEffect, useState } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { getLocationDb } from '../lib/firebase';

// Subscribes to live trustian-location updates for a single booking,
// via Firebase Realtime Database at /booking-locations/{booking_id}.
//
// The hook returns `{ location, updatedAt, isLive }`:
//   • location   — latest { lat, lng } received, seeded with the
//                  last-known snapshot from the booking doc so the
//                  map renders immediately instead of waiting for
//                  the first frame.
//   • updatedAt  — Date of the last received frame (null until one lands).
//   • isLive     — true while the listener is attached and the project
//                  is properly configured.
//
// Pass `enabled: false` (e.g. booking is cancelled or reported) to
// detach the listener cleanly.
const useBookingLocationStream = ({ bookingId, initialLocation, enabled = true }) => {
  const [location, setLocation] = useState(initialLocation || null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [isLive, setIsLive] = useState(false);

  // Re-seed if the initial snapshot arrives after the first render
  // (bookings query resolving slower than the component mounting).
  useEffect(() => {
    if (initialLocation && !location) setLocation(initialLocation);
  }, [initialLocation, location]);

  useEffect(() => {
    if (!enabled || !bookingId) return undefined;

    const db = getLocationDb();
    if (!db) {
      console.warn('[track] RTDB not configured — Track page will use only the seeded snapshot');
      return undefined;
    }

    const locationRef = ref(db, `booking-locations/${bookingId}`);

    const onValueChange = (snapshot) => {
      const v = snapshot.val();
      if (!v) {
        // Trip ended (Flebo removed the entry on cleanup). Keep showing
        // the last seen position; just mark the stream not-live.
        setIsLive(false);
        return;
      }
      if (typeof v.lat !== 'number' || typeof v.lng !== 'number') return;
      setIsLive(true);
      setLocation({ lat: v.lat, lng: v.lng });
      setUpdatedAt(v.updated_at ? new Date(v.updated_at) : new Date());
    };

    const onError = (err) => {
      console.warn('[track] RTDB error:', err?.message || err);
      setIsLive(false);
    };

    onValue(locationRef, onValueChange, onError);

    return () => {
      off(locationRef, 'value', onValueChange);
      setIsLive(false);
    };
  }, [bookingId, enabled]);

  return { location, updatedAt, isLive };
};

export default useBookingLocationStream;
